import React, { useState } from 'react';
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Collapse from "react-bootstrap/Collapse";
import { useTranslation } from "react-i18next";
import xor from 'lodash/xor';
import map from 'lodash/map';
import { SORT_PRICE, CAR_GROUP_ORDER } from '../../constants/default';
import CheckboxList from './CheckboxList';
import './styles.scss';
import { getAllFiltersWithCounts } from '../../utils/filterUtils';
import { trackEvent } from '../../utils/analytics';

const FilterWidget = ({ dir, data = [], activeFilters, onChange, onClear, isAirport, locationSlug, pickupDate, dropoffDate }) => {
  const { t, i18n } = useTranslation(); // eslint-disable-line
  const locale = i18n.language;
  const rtl = i18n.dir() === 'rtl';

  // State for collapsed groups - all groups start expanded by default
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Toggle collapse state for a group
  const toggleGroupCollapse = (groupTitle) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  // Always extract sortPrice as a single value from array (due to normalization)
  const sortPrice = Array.isArray(activeFilters.sortPrice) ? activeFilters.sortPrice[0] : activeFilters.sortPrice;

  const changeSort = value => () => {
    onChange({ sortPrice: [value] }); // always set as array
    trackEvent('Filter Changed', {
      'event_category': 'Filters',
      'event_label': 'sortPrice',
      'value': value
    });
  };

  const changeParam = param => value => () => {
    const filterType = allFilters[param]?.type || 'checkbox';
    let newValue;
    if (filterType === 'radio') {
      newValue = [value];
    } else {
      let oldValue = activeFilters[param] || [];
      newValue = xor(oldValue, [value]);
    }
    onChange({ [param]: newValue });
    trackEvent('Filter Changed', {
      'event_category': 'Filters',
      'event_label': param,
      'value': value
    });
  };

  const changeAndAbove = param => value => () => {
    const index = CAR_GROUP_ORDER.indexOf(value);
    const aboveValues = CAR_GROUP_ORDER.slice(index);
    let oldValue = activeFilters[param] || [];
    const allSelected = aboveValues.every(v => oldValue.includes(v));
    let newValue;
    if (allSelected) {
      // deselect them
      newValue = oldValue.filter(v => !aboveValues.includes(v));
    } else {
      // select them, keep others
      newValue = [...new Set([...oldValue, ...aboveValues])];
    }
    onChange({ [param]: newValue });
    trackEvent('And Above Filter Changed', {
      'event_category': 'Filters',
      'event_label': param,
      'value': value
    });
  };

  // Use the new utility to get all filters with counts
  const allFilters = getAllFiltersWithCounts({
    data,
    values: activeFilters,
    isAirport,
    locale,
    pickupDate,
    dropoffDate
  });

  // Define filter groups with emojis
  const filterGroups = [
    {
      title: 'carAttributes',
      emoji: '🚗',
      filters: ['transmission', 'carGroup', 'carType', 'seats', 'luggageCapacity', 'features']
    },
    {
      title: 'supplierAttributes', 
      emoji: '🏢',
      filters: ['recommendedSupplier', 'deskLocation', 'localSupplier']
    },
    {
      title: 'rentalTerms',
      emoji: '📋',
      filters: ['mileage', 'insurance', 'cancellationTerms', 'payNow', 'additionalDriverIncluded', 'winterEquipmentIncluded']
    }
  ];

  const renderFilterGroup = (groupTitle, groupEmoji, filterKeys) => {
    const groupFilters = filterKeys.filter(key => allFilters[key]).map(key => allFilters[key]);
    
    if (groupFilters.length === 0) return null;
    
    const isCollapsed = collapsedGroups[groupTitle] || false;
    
    return (
      <div key={groupTitle} className="filter-group mb-4">
        <div 
          className="filter-group-header d-flex align-items-center justify-content-between mb-3 p-2 bg-light rounded"
          onClick={() => toggleGroupCollapse(groupTitle)}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center">
            <span className={`filter-group-emoji ${rtl ? 'ml-2' : 'mr-2'}`} style={{ fontSize: '1.2em' }}>{groupEmoji}</span>
            <h6 className="filter-group-title mb-0 fw-bold text-primary">{t(groupTitle)}</h6>
          </div>
          <span className="filter-group-toggle" style={{ 
            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.3s ease',
            fontSize: '0.8em',
            color: '#6c757d'
          }}>
            ▼
          </span>
        </div>
        <Collapse in={!isCollapsed}>
          <div>
            {groupFilters.map(item => (
              <CheckboxList
                key={item.title}
                name={item.title}
                element={item}
                value={activeFilters[item.title]}
                onChange={changeParam(item.title)}
                onAndAbove={changeAndAbove(item.title)}
                locationSlug={locationSlug}
              />
            ))}
          </div>
        </Collapse>
      </div>
    );
  };

  const filterContent = (
    <>
      <Card>
        <Card.Header>
          <svg className='mx-2' height="24" width="24" fill='#007bff' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8L32 224c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9l128-128zm0 429.3l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8l256 0c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-128 128c-12.5 12.5-32.8 12.5-45.3 0z" /></svg>
          {t('sortBy')}
        </Card.Header>
        <Card.Body>
          {SORT_PRICE.items.map(item => (
            <Form.Check
              dir={dir}
              key={item.value}
              type="radio"
              id={item.value}
              name="priceOrder"
              checked={sortPrice === item.value}
              label={t(item.title)}
              onChange={changeSort(item.value)}
            />
          ))}
        </Card.Body>
      </Card>
      <Card>
        <Card.Header>
          <svg className='mx-2' height="24" width="24" fill='#007bff' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M3.9 54.9C10.5 40.9 24.5 32 40 32l432 0c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9 320 448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6l0-79.1L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z" /></svg>
          {t('filter')}
        </Card.Header>
        <Card.Body className="filter-content">
          {filterGroups.map(group => renderFilterGroup(group.title, group.emoji, group.filters))}
          <Card.Header>
          </Card.Header>
          <Card.Body>
            <Button variant="outline-primary" block onClick={onClear}>❌ {t('clearFilters')}</Button>
          </Card.Body>
        </Card.Body>
      </Card>
    </>
  );

  return (
    <div className="filter-section mb-3">
      {filterContent}
    </div>
  );
};

export default FilterWidget;
